import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import React, { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetSolutionOffering } from "../../../../api/services/programTemplate/programTemplateApis";
import {
  FormController,
  MuiBox,
  MuiDateField,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
} from "../../../../components";
import MuiChip from "../../../../components/chip/MuiChip";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../components/statusBadge/StatusBadge";
import { MAX_LENGTH_50, PAGE_URLS } from "../../../../helpers/constants";
import {
  DATE_FORMAT,
  LANGUAGE_CODE,
} from "../../../../helpers/constants/common";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../helpers/types/common";
import {
  buildActionMenuItems,
  compareDates,
  convertToUserFormat,
  getPropertyFromArray,
  getValueOrBlank,
  isDateInRange,
  isEmpty,
  isNotEmpty,
  isSolutionOfferingAllowedForTransfer,
} from "../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import {
  ProgramSearchResultProps,
  ProgramSearchResultRowValue,
  SearchProgramResultFormKey,
  searchTypeKey,
  UniqueProgramSearchResultFiltersTypes,
} from "../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { InputTransferDetails } from "../inputTransferDetails/InputTransferDetails";
import { ProgramSearchResultWrapper } from "./ProgramSearchResult.style";

export const ProgramSearchResult: FC<ProgramSearchResultProps> = ({
  rows,
  isSearch,
  pageId,
  searchType,
}) => {
  const {
    fetchLabel,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
    commonLabels,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { hasModulePermission } = usePermissions();

  const dispatch = useDispatch();

  const {
    data: solutionOfferings,
    isLoading: offeringPending,
    isError: isSolutionOfferingError,
  } = useGetSolutionOffering(LANGUAGE_CODE.EN_US, false, true);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: offeringPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [offeringPending]);

  useEffect(() => {
    if (isSolutionOfferingError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSolutionOfferingError]);

  const tableRef = React.useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [uniqueCountries, setUniqueCountries] = useState<LabelValue[]>([]);
  const [programId, setProgramId] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [showTransferModal, setShowTransferModal] = useState(false);

  const [uniqueFilters, setUniqueFilters] =
    useState<UniqueProgramSearchResultFiltersTypes>(() => ({
      programStatus: [],
      clientManagedStatus: [],
      solutionCategory: [],
      lineOfBusiness: [],
    }));

  // updated sitecore data
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    ProgramSearchResultRowValue[]
  >([]);

  const navigate = useNavigate();

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { control, setValue, getValues, reset } =
    useForm<ProgramSearchResultRowValue>({
      mode: "all",
      defaultValues: {
        [SearchProgramResultFormKey.participantName]: "",
        [SearchProgramResultFormKey.programName]: "",
        [SearchProgramResultFormKey.keyNumber]: "",
        [SearchProgramResultFormKey.participantType]: "",
        [SearchProgramResultFormKey.programStatus]: "",
        [SearchProgramResultFormKey.companyName]: "",
        [SearchProgramResultFormKey.clientManagedStatus]: "",
        [SearchProgramResultFormKey.participantEmail]: "",
        [SearchProgramResultFormKey.participantMobileNumber]: "",
        [SearchProgramResultFormKey.projectManager]: "",
        [SearchProgramResultFormKey.country]: [],
        [SearchProgramResultFormKey.filterCountryName]: "",
        [SearchProgramResultFormKey.createdDate]: "",
        [SearchProgramResultFormKey.billingContactName]: "",
        [SearchProgramResultFormKey.hrContactName]: "",
        [SearchProgramResultFormKey.lineOfBusiness]: "",
        [SearchProgramResultFormKey.clientRelationShipOwner]: "",
        [SearchProgramResultFormKey.solutionCategory]: "",
        [SearchProgramResultFormKey.programStartDate]: "",
        [SearchProgramResultFormKey.projectKey]: "",
        [SearchProgramResultFormKey.projectName]: "",
      },
    });
  const filters = useWatch({ control });

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  useEffect(() => {
    resetPagination(true);
    const myData = [...rows];

    const uniqueCountries = Array.from(
      new Set(
        myData
          .map((item) => item?.countryName ?? "")
          .filter((country) => country !== ""),
      ),
    ).map((country) => ({ value: country, label: country }));

    setUniqueCountries(uniqueCountries);
    reset();
  }, [rows]);

  const getUniqueFilters = (
    data: ProgramSearchResultRowValue[],
    key: string,
  ): LabelValue[] => {
    return Array.from(
      new Set(
        data
          .filter((item) => item[key]) // Filter out null or undefined values
          .map((item) => item[key]) // Extract values for the given key
          .reduce((acc, val) => acc.concat(val), []), // Flatten the array if values are arrays
      ),
    ).map((value) => ({ value: value as string, label: value as string })); // Format as value-label pairs
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = [...rows];

      localData.forEach((data) => {
        data.statusKey = data.programStatus;

        data.programStatus = fetchValueByKeyOfDropdown(
          programSearchSiteCoreKeys.searchFormKey,
          programSearchSiteCoreKeys.programStatusList,
          data.programStatus,
        );

        data.clientManagedStatus = data.clientManagedStatus
          ? fetchValueByKeyOfDropdown(
              programSearchSiteCoreKeys.searchFormKey,
              programSearchSiteCoreKeys.clientManagedStatusList,
              data.clientManagedStatus,
            )
          : "";
      });
      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [SearchProgramResultFormKey.programStatus]: getUniqueFilters(
          localData,
          SearchProgramResultFormKey.programStatus,
        ),
        [SearchProgramResultFormKey.clientManagedStatus]: getUniqueFilters(
          localData,
          SearchProgramResultFormKey.clientManagedStatus,
        ),
        [SearchProgramResultFormKey.solutionCategory]: getUniqueFilters(
          localData,
          SearchProgramResultFormKey.solutionCategory,
        ),
        [SearchProgramResultFormKey.lineOfBusiness]: getUniqueFilters(
          localData,
          SearchProgramResultFormKey.lineOfBusiness,
        ),
      }));
    }
  }, [rows, siteCoreLoading]);

  const checkFilterMatch = (inputValue: string, rowItem: string) => {
    return isEmpty(inputValue?.toLowerCase())
      ? true
      : !isEmpty(rowItem) &&
          rowItem?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const {
        participantName,
        programName,
        keyNumber,
        participantType,
        programStatus,
        companyName,
        clientManagedStatus,
        participantEmail,
        participantMobileNumber,
        projectManager,
        filterCountryName,
        createdDate,
        billingContactName,
        hrContactName,
        lineOfBusiness,
        clientRelationShipOwner,
        solutionCategory,
        programStartDate,
        projectKey,
        projectName,
      } = getValues();

      const myData = allUpdatedRows.filter((item) => {
        const programNameMatch = checkFilterMatch(
          getValueOrBlank(programName),
          getValueOrBlank(item.programName),
        );

        const keyNumberMatch = checkFilterMatch(keyNumber, item.keyNumber);

        const projectKeyMatch = checkFilterMatch(
          getValueOrBlank(projectKey),
          getValueOrBlank(item.projectKey),
        );

        const projectNameMatch = checkFilterMatch(
          getValueOrBlank(projectName),
          getValueOrBlank(item.projectName),
        );

        const companyNameMatch = checkFilterMatch(
          companyName,
          item.companyName,
        );

        const participantNameMatch = checkFilterMatch(
          getValueOrBlank(participantName),
          getValueOrBlank(item.participantName),
        );

        const participantMobileNumberMatch = checkFilterMatch(
          getValueOrBlank(participantMobileNumber),
          getValueOrBlank(item.participantMobileNumber),
        );

        const participantEmailMatch = checkFilterMatch(
          getValueOrBlank(participantEmail),
          getValueOrBlank(item.participantEmail),
        );

        const projectManagerMatch = checkFilterMatch(
          getValueOrBlank(projectManager),
          getValueOrBlank(item.projectManager),
        );

        const billingContactNameMatch = checkFilterMatch(
          getValueOrBlank(billingContactName),
          getValueOrBlank(item.billingContactName),
        );

        const hrContactNameMatch = checkFilterMatch(
          getValueOrBlank(hrContactName),
          getValueOrBlank(item.hrContactName),
        );

        const lineOfBusinessMatch = checkFilterMatch(
          getValueOrBlank(lineOfBusiness),
          getValueOrBlank(item.lineOfBusiness),
        );

        const clientRelationShipOwnerMatch = checkFilterMatch(
          getValueOrBlank(clientRelationShipOwner),
          getValueOrBlank(item.clientRelationShipOwner),
        );

        const participantTypeMatch = checkFilterMatch(
          getValueOrBlank(participantType),
          getValueOrBlank(item.candidateType),
        );

        const solutionCategoryMatch = checkFilterMatch(
          getValueOrBlank(solutionCategory),
          getValueOrBlank(item.solutionCategory),
        );

        const statusMatch =
          isEmpty(programStatus) ||
          item.programStatus?.toLowerCase() === programStatus?.toLowerCase();

        const clientManagedStatusMatch =
          isEmpty(clientManagedStatus) ||
          item.clientManagedStatus?.toLowerCase() ===
            clientManagedStatus?.toLowerCase();

        const countryMatch =
          !filterCountryName?.length ||
          filterCountryName.some((c: string) =>
            item?.countryName?.toLowerCase().includes(c.toLowerCase()),
          );

        let isDateMatch = compareDates(
          getValueOrBlank(createdDate),
          getValueOrBlank(item?.createdDate),
          getValueOrBlank(userCountryDateFormat),
        );

        let isProgramStartDateMatch = true;

        if (programStartDate) {
          if (!item?.programStartDate) {
            isProgramStartDateMatch = false;
          } else {
            const selectedDate = dayjs(programStartDate, userCountryDateFormat);
            const startOfSelectedDateMonth = selectedDate.startOf("month");
            const endOfSelectedDateMonth = selectedDate.endOf("month");

            const itemDate = dayjs(item?.programStartDate);

            isProgramStartDateMatch = isDateInRange(
              itemDate,
              startOfSelectedDateMonth,
              endOfSelectedDateMonth,
            );
          }
        }

        return (
          programNameMatch &&
          keyNumberMatch &&
          companyNameMatch &&
          statusMatch &&
          isDateMatch &&
          isProgramStartDateMatch &&
          countryMatch &&
          participantNameMatch &&
          participantMobileNumberMatch &&
          participantEmailMatch &&
          projectManagerMatch &&
          billingContactNameMatch &&
          hrContactNameMatch &&
          lineOfBusinessMatch &&
          clientRelationShipOwnerMatch &&
          participantTypeMatch &&
          clientManagedStatusMatch &&
          solutionCategoryMatch &&
          projectKeyMatch &&
          projectNameMatch
        );
      });
      setFilteredResults([...myData]);
      resetPagination();
    }
  }, [filters]);

  const showTransferButton = (status: string, solutionOffering: string) => {
    return (
      status === STATUS_BADGE_TYPE.active &&
      isSolutionOfferingAllowedForTransfer(
        getPropertyFromArray(solutionOfferings ?? [], "name"),
        solutionOffering,
      )
    );
  };

  const columns = [
    {
      id: SearchProgramResultFormKey.participantName,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.participantNameLabel,
      ),
      isEmptyValue: true,
      isSortVisible: true,
      className: "cell-program-Key-number",
      render: (row: RowProps) =>
        !row.participantName ? null : (
          <MuiLink
            href={`${PAGE_URLS.VIEW_PROGRAM}/${row.id}`}
            isOpenNewTab
            id={`${pageId}-view-program`}
            data-testid="view-program-link"
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link"
                text={row.participantName}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
    },
    {
      id: SearchProgramResultFormKey.programName,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.programNameLabel,
      ),
      className: "cell-program-name",
      isSortVisible: true,
    },
    {
      id: SearchProgramResultFormKey.keyNumber,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.programKeyLabel,
      ),
      isSortVisible: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.participantType,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.participantTypeLabel,
      ),
      isSortVisible: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.programStatus,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.programStatusResult,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-status",
      render: (row: any) => (
        <StatusBadge status={row.statusKey} label={row.programStatus} />
      ),
    },
    {
      id: SearchProgramResultFormKey.companyName,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.companyNameLabel,
      ),
      isSortVisible: true,
      className: "cell-company-name",
    },
    {
      id: SearchProgramResultFormKey.clientManagedStatus,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.clientManagedStatusLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-status",
      render: (row: RowProps) =>
        row.clientManagedStatus ? (
          <MuiChip
            data={[
              {
                label: row.clientManagedStatus,
                value: row.clientManagedStatus,
              },
            ]}
            isEditable={false}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
          />
        ) : (
          <></>
        ),
    },
    ...(searchType === searchTypeKey.programs
      ? [
          {
            id: SearchProgramResultFormKey.projectKey,
            label: fetchLabel(
              programSearchSiteCoreKeys.resultFormKey,
              programSearchSiteCoreKeys.projectKeyLabel,
            ),
            isSortVisible: true,
            isEmptyValue: true,
          },
          {
            id: SearchProgramResultFormKey.projectName,
            label: fetchLabel(
              programSearchSiteCoreKeys.resultFormKey,
              programSearchSiteCoreKeys.projectNameLabel,
            ),
            isSortVisible: true,
            isEmptyValue: true,
          },
        ]
      : []),
    {
      id: SearchProgramResultFormKey.participantEmail,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.participantEmailLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.participantMobileNumber,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.participantMobileNumberLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.projectManager,
      label: fetchLabel(
        programSearchSiteCoreKeys.searchFormKey,
        programSearchSiteCoreKeys.projectManagerLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.country,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.countryLabel,
      ),
      isSortVisible: true,
      className: "cell-counry",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {row.countryName}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: SearchProgramResultFormKey.createdDate,
      label: commonLabels.createdDate,
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {!isEmpty(row.createdDate)
              ? convertToUserFormat(row.createdDate, userCountryDateFormat)
              : ""}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: SearchProgramResultFormKey.billingContactName,
      label: fetchLabel(
        programSearchSiteCoreKeys.searchFormKey,
        programSearchSiteCoreKeys.billingContactLastNameLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.hrContactName,
      label: fetchLabel(
        programSearchSiteCoreKeys.searchFormKey,
        programSearchSiteCoreKeys.hRContactLastNameLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.lineOfBusiness,
      label: fetchLabel(
        programSearchSiteCoreKeys.searchFormKey,
        programSearchSiteCoreKeys.productLineLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.clientRelationShipOwner,
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.localClientRelationshipOwnerLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.solutionCategory,
      label: fetchLabel(
        programSearchSiteCoreKeys.searchFormKey,
        programSearchSiteCoreKeys.solutionCategoryLabel,
      ),
      isSortVisible: true,
      isEmptyValue: true,
      className: "cell-program-Key-number",
    },
    {
      id: SearchProgramResultFormKey.programStartDate,
      label: fetchLabel(
        programSearchSiteCoreKeys.searchFormKey,
        programSearchSiteCoreKeys.programStartMonthOrYearLabel,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {!isEmpty(row.programStartDate)
              ? dayjs(row.programStartDate).format(DATE_FORMAT.MMMM_YYYY)
              : ""}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "acton",
      label: fetchLabel(
        programSearchSiteCoreKeys.resultFormKey,
        programSearchSiteCoreKeys.actionLabel,
      ),
      render: (row: RowProps) => {
        const transferText = fetchLabel(
          programSearchSiteCoreKeys.resultFormKey,
          programSearchSiteCoreKeys.transferParticipantOption,
        );

        const menuItems = buildActionMenuItems(
          row,
          [
            {
              show: (row) => row?.isEditable,
              create: (row) => ({
                onClick: () => {
                  navigate(`${PAGE_URLS.EDIT_PROGRAM}/${row.id}`);
                },
                dataTestId: "edit",
                label: commonLabels.edit,
                ariaLabel: commonLabels.edit,
              }),
            },
            {
              show: (row) =>
                row?.isEditable &&
                showTransferButton(row.statusKey, row.solutionOffering),
              create: (row) => ({
                onClick: () => {
                  setShowTransferModal(true);
                  setProgramId(row?.id);
                  setParticipantName(row?.participantName);
                },
                dataTestId: "Transfer Participant",
                label: transferText,
                ariaLabel: transferText,
              }),
            },
          ],
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const handleMuiDateChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(SearchProgramResultFormKey.programStartDate, dateValue);
  };

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm = [
    <FormController
      control={control}
      name={SearchProgramResultFormKey.participantName}
      key={SearchProgramResultFormKey.participantName}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.participantName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.programName}
      key={SearchProgramResultFormKey.programName}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.programName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.keyNumber}
      key={SearchProgramResultFormKey.keyNumber}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.keyNumber}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.participantType}
      key={SearchProgramResultFormKey.participantType}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.participantType}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.programStatus}
      key={SearchProgramResultFormKey.programStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.programStatus}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SearchProgramResultFormKey.programStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.companyName}
      key={SearchProgramResultFormKey.companyName}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.companyName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.clientManagedStatus}
      key={SearchProgramResultFormKey.clientManagedStatus}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.clientManagedStatus}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${SearchProgramResultFormKey.clientManagedStatus}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    ...(searchType === searchTypeKey.programs
      ? [
          <FormController
            control={control}
            name={SearchProgramResultFormKey.projectKey}
            key={SearchProgramResultFormKey.projectKey}
          >
            <MuiTextField
              id={`${pageId}-${SearchProgramResultFormKey.projectKey}`}
              startAdornment={renderSearchIcon()}
              maxLength={MAX_LENGTH_50}
              placeholder={commonLabels.search}
              showCloseIcon
              shouldTrimSpaces={false}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>,
          <FormController
            control={control}
            name={SearchProgramResultFormKey.projectName}
            key={SearchProgramResultFormKey.projectName}
          >
            <MuiTextField
              id={`${pageId}-${SearchProgramResultFormKey.projectName}`}
              startAdornment={renderSearchIcon()}
              maxLength={MAX_LENGTH_50}
              placeholder={commonLabels.search}
              showCloseIcon
              shouldTrimSpaces={false}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>,
        ]
      : []),
    <FormController
      control={control}
      name={SearchProgramResultFormKey.participantEmail}
      key={SearchProgramResultFormKey.participantEmail}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.participantEmail}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.participantMobileNumber}
      key={SearchProgramResultFormKey.participantMobileNumber}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.participantMobileNumber}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.projectManager}
      key={SearchProgramResultFormKey.projectManager}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.projectManager}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.filterCountryName}
      key={SearchProgramResultFormKey.filterCountryName}
    >
      <MuiMultiSelect
        label=""
        disabled={false}
        showSearch={true}
        showChip={false}
        textFieldWidth={0}
        options={uniqueCountries}
        id={`${pageId}-${SearchProgramResultFormKey.country}`}
        selectPlaceHolder={commonLabels.selectPlaceHolder}
        searchPlaceHolder={commonLabels.search}
        selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
        doneText={commonLabels.done}
        allARIAProps={multiSelectARIALabels}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.createdDate}
      key={SearchProgramResultFormKey.createdDate}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MuiDateField
          format={userCountryDateFormat}
          name="createdDate"
          clearable
          value={
            getValues(SearchProgramResultFormKey.createdDate) !== ""
              ? dayjs(
                  getValues(SearchProgramResultFormKey.createdDate),
                  userCountryDateFormat,
                )
              : null
          }
          onChange={(newValue) => {
            setValue(
              SearchProgramResultFormKey.createdDate,
              newValue?.format(userCountryDateFormat) ?? "",
            );
          }}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.billingContactName}
      key={SearchProgramResultFormKey.billingContactName}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.billingContactName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.hrContactName}
      key={SearchProgramResultFormKey.hrContactName}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.hrContactName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.lineOfBusiness}
      key={SearchProgramResultFormKey.lineOfBusiness}
    >
      <MuiSelect
        label={""}
        options={uniqueFilters.lineOfBusiness}
        visibleDefaultSelectOption
        value=""
        id={`${pageId}-${SearchProgramResultFormKey.lineOfBusiness}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.clientRelationShipOwner}
      key={SearchProgramResultFormKey.clientRelationShipOwner}
    >
      <MuiTextField
        id={`${pageId}-${SearchProgramResultFormKey.clientRelationShipOwner}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.solutionCategory}
      key={SearchProgramResultFormKey.solutionCategory}
    >
      <MuiSelect
        label={""}
        options={uniqueFilters.solutionCategory}
        visibleDefaultSelectOption
        value=""
        id={`${pageId}-${SearchProgramResultFormKey.solutionCategory}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={SearchProgramResultFormKey.programStartDate}
      key={SearchProgramResultFormKey.programStartDate}
    >
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        localeText={{
          fieldMonthPlaceholder: () =>
            fetchLabel(
              programSearchSiteCoreKeys.searchFormKey,
              programSearchSiteCoreKeys.monthPlaceholder,
            ),
          fieldYearPlaceholder: () =>
            fetchLabel(
              programSearchSiteCoreKeys.searchFormKey,
              programSearchSiteCoreKeys.yearPlaceholder,
            ),
        }}
      >
        <MuiDateField
          value={
            getValues().programStartDate !== ""
              ? dayjs(getValues().programStartDate, userCountryDateFormat)
              : null
          }
          format={DATE_FORMAT.MMM_YYYY_SLASH}
          clearable
          views={["month", "year"]}
          onChange={handleMuiDateChange}
          requiredARIALabel={commonARIALabel.required}
          selectDateARIALabel={commonARIALabel.selectDate}
        />
      </LocalizationProvider>
    </FormController>,
    <></>,
  ];

  return (
    <ProgramSearchResultWrapper>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {fetchComponentTitle(programSearchSiteCoreKeys.resultFormKey)}
          </MuiTypography>
        </MuiGrid>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={!siteCoreLoading ? tableHeaderForm : []}
              columns={columns}
              tableResults={[...filteredResults]}
              noDataInApi={rows.length === 0 && !isSearch}
              emptyMessage={commonLabels.applyNecessaryFiltersMessage}
              noResultMessage={commonLabels.noResult}
              priorityResults={[]}
              isLastColumnSticky
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
      {showTransferModal && (
        <InputTransferDetails
          pageId={pageId}
          open={showTransferModal}
          setShowInputTransferDetailsModal={setShowTransferModal}
          programId={programId}
          participantName={participantName}
        />
      )}
    </ProgramSearchResultWrapper>
  );
};
